import { camelCaseToSentenceCase, sentenceOrPascalToCamel } from "../../utils/string/stringCase";
import { ProjectPhase } from "../project-phase/ProjectPhase";

/** Structural/internal keys on a project dates group that are never a date field of their own. */
export const STRUCTURAL_DATE_KEYS = new Set<string>([
  "status",
  "overallStatus",
  "completedByUserUuid",
  "uuid",
  "sequence",
  "cycles",
]);

const DEFAULT_PHASE_BOUNDARY_KEYS = ["start", "end"] as const;

/**
 * The pair of keys a phase's dates group opens and closes with. Ongoing Monitoring spans its cycles,
 * so it spells its pair `overallStart` / `overallEnd` while `start` / `end` there belong to a single
 * cycle; every other phase uses `start` / `end`. This map is the only place the boundary key names
 * are written down — `PHASE_BOUNDARY_KEYS` is derived from it, so an addition or rename here reaches
 * every reader at once.
 */
export const PHASE_BOUNDARY_KEY_PAIR: Record<ProjectPhase, readonly [string, string]> = {
  [ProjectPhase.feasibility]: DEFAULT_PHASE_BOUNDARY_KEYS,
  [ProjectPhase.design]: DEFAULT_PHASE_BOUNDARY_KEYS,
  [ProjectPhase.implementation]: DEFAULT_PHASE_BOUNDARY_KEYS,
  [ProjectPhase.ongoingMonitoring]: ["overallStart", "overallEnd"],
};

/**
 * The boundary pair for a phase. Only a project workflow template's phases are `ProjectPhase` values;
 * every other template type names its stages freely, so the caller has to say which it holds rather
 * than let a stage that happens to be called "ongoingMonitoring" be read as the project phase. A
 * stage outside the map gets `start` / `end`, which is what a non-project dates group is built with.
 */
export const getPhaseBoundaryKeys = (phase: string, isProjectPhase: boolean): readonly [string, string] =>
  isProjectPhase
    ? (PHASE_BOUNDARY_KEY_PAIR[phase as ProjectPhase] ?? DEFAULT_PHASE_BOUNDARY_KEYS)
    : DEFAULT_PHASE_BOUNDARY_KEYS;

/**
 * Phase boundary dates. They open and close a phase rather than marking a point in time, so readers
 * that list a phase's dates exclude them — the overview shows them elsewhere, and the roadmap draws
 * them as bars. Cycle `start`/`end` share these key names and are excluded for the same reason. The
 * workflow-template deadline picker offers them instead of excluding them, but asks the same
 * membership question, so it reads the same set.
 */
export const PHASE_BOUNDARY_KEYS = new Set<string>(Object.values(PHASE_BOUNDARY_KEY_PAIR).flat());

/**
 * How each boundary key reads once the phase name is in front of it. Driven by the key rather than
 * by a start/end test, because `start` and `overallStart` can both be offered for Ongoing Monitoring
 * and would otherwise render the same label against two different stored keys. "Phase start/end"
 * matches what the overview calls `ongoingMonitoring.overallStart` in PROJECT_DATE_FIELD_METADATA.
 */
const BOUNDARY_KEY_SUFFIX: Record<string, string> = {
  start: "start",
  end: "end",
  overallStart: "phase start",
  overallEnd: "phase end",
};

/**
 * A boundary key only says "this phase's start/end" — the phase it belongs to comes from the step,
 * so the label has to be built from the pair. Used for the deadline dropdown options, the step list
 * and the read-only panel, so a saved value always reads as the option that was picked.
 */
export const phaseBoundaryDeadlineLabel = (phase: string, boundaryKey: string): string => {
  const phaseLabel = camelCaseToSentenceCase(sentenceOrPascalToCamel(phase));
  const suffix = BOUNDARY_KEY_SUFFIX[boundaryKey] ?? (boundaryKey.toLowerCase().endsWith("start") ? "start" : "end");
  return `${phaseLabel} ${suffix} date`;
};
